/**
 * Security Settings Page (Story 0.8, 0.9, 1.10, 1.14)
 *
 * Account security, data export, and deletion management.
 * Contains:
 * - Password Change/Set card (Story 1.10)
 * - Active Sessions / Log out everywhere card (Story 1.14)
 * - Data Export card (Story 0.9 — GDPR Article 20)
 * - Danger Zone with account deletion/cancellation (Story 0.8)
 */

import { useState, useCallback, useRef, useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { ReportBreadcrumb } from "../../../components/reports/ReportBreadcrumb";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Button,
  Input,
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@provenleap/ui";
import { apiGet, apiPost, apiPatch } from "../../../lib/apiClient";
import { logoutAll } from "../../../lib/auth";
import { DeleteAccountDialog } from "../../../components/account/DeleteAccountDialog";

export const Route = createFileRoute("/_authenticated/settings/security")({
  component: SecuritySettingsPage,
});

interface AccountStatusResponse {
  data: {
    status: string;
    deletion_scheduled_at?: string;
    deletion_date?: string;
    deletion_cancelled_at?: string;
  };
}

interface ExportStatusResponse {
  data: {
    exportRequested: boolean;
    exportRequestedAt: string | null;
    exportReadyAt: string | null;
    expired: boolean;
  };
}

interface ProfileResponse {
  data: {
    hasPassword: boolean;
  };
}

// Mirror of getPasswordRequirements() from @provenleap/auth/password/validation.ts
// Keep in sync if server-side rules change.
const PASSWORD_REQUIREMENTS = [
  "At least 8 characters",
  "At least 1 uppercase letter",
  "At least 1 number",
];

// Mirror of validatePassword() from @provenleap/auth/password/validation.ts
// Keep in sync if server-side rules change.
function validatePasswordClient(password: string): Record<string, string> {
  const errors: Record<string, string> = {};
  if (password.length < 8) {
    errors.minLength = "Password must be at least 8 characters";
  }
  if (!/[A-Z]/.test(password)) {
    errors.uppercase = "Password must contain at least 1 uppercase letter";
  }
  if (!/[0-9]/.test(password)) {
    errors.number = "Password must contain at least 1 number";
  }
  return errors;
}

function SecuritySettingsPage() {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [logoutAllDialogOpen, setLogoutAllDialogOpen] = useState(false);
  const [logoutAllPending, setLogoutAllPending] = useState(false);
  const [logoutAllToast, setLogoutAllToast] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Password form state (Story 1.10)
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>({});
  const [passwordToast, setPasswordToast] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const passwordToastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const logoutAllToastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (passwordToastTimer.current) clearTimeout(passwordToastTimer.current);
      if (logoutAllToastTimer.current) clearTimeout(logoutAllToastTimer.current);
    };
  }, []);

  const showPasswordToast = (type: "success" | "error", message: string) => {
    setPasswordToast({ type, message });
    if (passwordToastTimer.current) clearTimeout(passwordToastTimer.current);
    passwordToastTimer.current = setTimeout(() => setPasswordToast(null), 3000);
  };

  // Fetch profile to determine if user has password (Story 1.10)
  const profileQuery = useQuery({
    queryKey: ["user", "profile"],
    queryFn: async () => {
      const response = await apiGet("/api/user/profile");
      if (!response.ok) return null;
      const data = (await response.json()) as ProfileResponse;
      return data.data;
    },
  });

  const hasPassword = profileQuery.data?.hasPassword ?? null;

  // Password change/set mutation (Story 1.10)
  const passwordMutation = useMutation({
    mutationFn: async (body: { currentPassword?: string; newPassword: string }) => {
      const response = await apiPatch("/api/user/password", body);
      if (!response.ok) {
        const errorBody = await response.json() as {
          detail?: string;
          errors?: Array<{ field: string; message: string }>;
        };
        // Extract field-specific errors — surface all of them
        if (errorBody.errors && errorBody.errors.length > 0) {
          const messages = errorBody.errors.map((e) => e.message);
          throw new Error(messages.join(". "));
        }
        throw new Error(
          errorBody.detail || "Failed to update password"
        );
      }
      return response.json();
    },
    onSuccess: () => {
      showPasswordToast("success", hasPassword ? "Password changed successfully" : "Password set successfully");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setPasswordErrors({});
      // Invalidate profile to refresh hasPassword state
      queryClient.invalidateQueries({ queryKey: ["user", "profile"] });
    },
    onError: (error: Error) => {
      showPasswordToast("error", error.message);
    },
  });

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Client-side validation
    const errors: Record<string, string> = {};

    if (hasPassword && !currentPassword) {
      errors.currentPassword = "Current password is required";
    }

    const newPwErrors = validatePasswordClient(newPassword);
    if (Object.keys(newPwErrors).length > 0) {
      errors.newPassword = Object.values(newPwErrors).join(". ");
    }

    if (newPassword !== confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    if (Object.keys(errors).length > 0) {
      setPasswordErrors(errors);
      return;
    }

    setPasswordErrors({});

    const body: { currentPassword?: string; newPassword: string } = {
      newPassword,
    };
    if (hasPassword) {
      body.currentPassword = currentPassword;
    }

    passwordMutation.mutate(body);
  };

  // Logout all sessions handler (Story 1.14)
  const handleLogoutAll = async () => {
    setLogoutAllPending(true);
    const result = await logoutAll();
    setLogoutAllPending(false);
    setLogoutAllDialogOpen(false);

    if (result !== null) {
      setLogoutAllToast({
        type: "success",
        message: "All other sessions terminated",
      });
    } else {
      setLogoutAllToast({
        type: "error",
        message: "Failed to terminate other sessions. Please try again.",
      });
    }
    if (logoutAllToastTimer.current) clearTimeout(logoutAllToastTimer.current);
    logoutAllToastTimer.current = setTimeout(() => setLogoutAllToast(null), 3000);
  };

  // Fetch account status for deletion state (AC#18 re-request warning)
  const accountStatusQuery = useQuery({
    queryKey: ["account-status"],
    queryFn: async () => {
      const response = await apiGet("/api/account/status");
      if (!response.ok) return null;
      const data = (await response.json()) as AccountStatusResponse;
      return data.data;
    },
  });

  const accountStatus = accountStatusQuery.data;

  // Fetch export status (Story 0.9)
  const exportStatusQuery = useQuery({
    queryKey: ["export-status"],
    queryFn: async () => {
      const response = await apiGet("/api/account/export-status");
      if (!response.ok) return null;
      const data = (await response.json()) as ExportStatusResponse;
      return data.data;
    },
    refetchInterval: (query) => {
      // Poll every 5s while export is in progress
      const data = query.state.data;
      if (data?.exportRequested && !data?.exportReadyAt) {
        return 5000;
      }
      return false;
    },
  });

  const exportStatus = exportStatusQuery.data;
  const isExportInProgress =
    exportStatus?.exportRequested && !exportStatus?.exportReadyAt;
  const isExportReady =
    exportStatus?.exportReadyAt && !exportStatus?.expired;

  // Request export mutation (Story 0.9)
  const exportMutation = useMutation({
    mutationFn: async () => {
      const response = await apiPost("/api/account/export");
      if (!response.ok) {
        const errorBody = await response.json();
        if (response.status === 409) {
          throw new Error("A data export is already in progress.");
        }
        throw new Error(
          (errorBody as { detail?: string }).detail ||
            "Failed to request data export"
        );
      }
      return response.json();
    },
    onSuccess: () => {
      setExportError(null);
      queryClient.invalidateQueries({ queryKey: ["export-status"] });
    },
    onError: (error: Error) => {
      setExportError(error.message);
    },
  });

  // Cancel deletion mutation (AC#16)
  const cancelDeletionMutation = useMutation({
    mutationFn: async () => {
      const response = await apiPost("/api/account/cancel-deletion");
      if (!response.ok) {
        const errorBody = await response.json();
        throw new Error(
          (errorBody as { detail?: string }).detail ||
            "Failed to cancel deletion"
        );
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["account-status"] });
    },
  });

  const [downloadLoading, setDownloadLoading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const handleDownload = useCallback(async () => {
    setDownloadLoading(true);
    setDownloadError(null);
    try {
      const response = await apiGet("/api/account/export/download");
      if (!response.ok) {
        throw new Error("Download failed. Please try again.");
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "provenleap-export.json";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setDownloadError(
        err instanceof Error ? err.message : "Download failed."
      );
    } finally {
      setDownloadLoading(false);
    }
  }, []);

  const isPendingDeletion = accountStatus?.status === "pending_deletion";

  return (
    <div className="p-6 max-w-[800px] mx-auto">
      <ReportBreadcrumb items={[{ label: "Settings", to: "/settings" }, { label: "Security" }]} />

      {/* Password toast */}
      {passwordToast && (
        <div
          role="alert"
          aria-live="polite"
          data-testid="password-toast"
          className={`mb-4 p-3 rounded-md ${
            passwordToast.type === "success"
              ? "bg-green-50 text-green-800 border border-green-200"
              : "bg-red-50 text-red-800 border border-red-200"
          }`}
        >
          {passwordToast.message}
        </div>
      )}

      {/* Password Change/Set Card (Story 1.10) */}
      {hasPassword !== null && (
        <Card data-testid="password-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <LockIcon className="h-5 w-5" />
              {hasPassword ? "Change Password" : "Set a Password"}
            </CardTitle>
            <CardDescription>
              {hasPassword
                ? "Update your password to keep your account secure. You'll remain logged in on this device."
                : "Set a password to enable email/password login alongside your OAuth account."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              {hasPassword && (
                <div className="space-y-2">
                  <label htmlFor="current-password" className="text-sm font-medium text-foreground">
                    Current Password
                  </label>
                  <Input
                    id="current-password"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => {
                      setCurrentPassword(e.target.value);
                      setPasswordErrors(({ currentPassword: _cp, ...rest }) => rest);
                    }}
                    data-testid="current-password-input"
                    autoComplete="current-password"
                  />
                  {passwordErrors.currentPassword && (
                    <p className="text-sm text-destructive" data-testid="current-password-error">
                      {passwordErrors.currentPassword}
                    </p>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <label htmlFor="new-password" className="text-sm font-medium text-foreground">
                  New Password
                </label>
                <Input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    setPasswordErrors(({ newPassword: _np, ...rest }) => rest);
                  }}
                  data-testid="new-password-input"
                  autoComplete="new-password"
                />
                {passwordErrors.newPassword && (
                  <p className="text-sm text-destructive" data-testid="new-password-error">
                    {passwordErrors.newPassword}
                  </p>
                )}
                <ul className="text-xs text-muted-foreground space-y-1 mt-1">
                  {PASSWORD_REQUIREMENTS.map((req) => (
                    <li key={req}>{req}</li>
                  ))}
                </ul>
              </div>

              <div className="space-y-2">
                <label htmlFor="confirm-password" className="text-sm font-medium text-foreground">
                  Confirm New Password
                </label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setPasswordErrors(({ confirmPassword: _cp, ...rest }) => rest);
                  }}
                  data-testid="confirm-password-input"
                  autoComplete="new-password"
                />
                {passwordErrors.confirmPassword && (
                  <p className="text-sm text-destructive" data-testid="confirm-password-error">
                    {passwordErrors.confirmPassword}
                  </p>
                )}
              </div>

              <div className="flex justify-end">
                <Button
                  type="submit"
                  disabled={passwordMutation.isPending}
                  data-testid="password-submit-btn"
                >
                  {passwordMutation.isPending
                    ? "Saving..."
                    : hasPassword
                      ? "Change Password"
                      : "Set Password"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Logout all toast */}
      {logoutAllToast && (
        <div
          role="alert"
          aria-live="polite"
          data-testid="logout-all-toast"
          className={`mb-4 p-3 rounded-md ${
            logoutAllToast.type === "success"
              ? "bg-green-50 text-green-800 border border-green-200"
              : "bg-red-50 text-red-800 border border-red-200"
          }`}
        >
          {logoutAllToast.message}
        </div>
      )}

      {/* Active Sessions Card (Story 1.14) */}
      <div className="mt-8">
        <Card data-testid="active-sessions-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldIcon className="h-5 w-5" />
              Active Sessions
            </CardTitle>
            <CardDescription>
              Manage your active sessions across all devices. Logging out everywhere will terminate all other sessions while keeping you logged in on this device.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Dialog open={logoutAllDialogOpen} onOpenChange={setLogoutAllDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="destructive"
                  disabled={logoutAllPending}
                  data-testid="logout-all-btn"
                >
                  {logoutAllPending ? "Logging out..." : "Log out everywhere"}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Log out everywhere?</DialogTitle>
                  <DialogDescription>
                    This will terminate all other sessions. You&apos;ll stay logged in on this device.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <DialogClose asChild>
                    <Button variant="ghost" data-testid="logout-all-cancel-btn">
                      Cancel
                    </Button>
                  </DialogClose>
                  <Button
                    variant="destructive"
                    onClick={handleLogoutAll}
                    disabled={logoutAllPending}
                    data-testid="logout-all-confirm-btn"
                  >
                    {logoutAllPending ? "Logging out..." : "Log out everywhere"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>
      </div>

      {/* Data Export — GDPR Article 20 (Story 0.9) */}
      <div className="mt-8">
        {isExportReady ? (
          <Card className="border-green-500" data-testid="data-export-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-700 dark:text-green-400">
                <DownloadIcon className="h-5 w-5" />
                Your Data Export is Ready
              </CardTitle>
              <CardDescription>
                Your data export is available for download. It will expire{" "}
                {exportStatus?.exportReadyAt
                  ? formatExpiry(exportStatus.exportReadyAt)
                  : "in 24 hours"}.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={handleDownload}
                disabled={downloadLoading}
                data-testid="download-export-btn"
              >
                {downloadLoading ? (
                  <>
                    <SpinnerIcon className="h-4 w-4 mr-2 animate-spin" />
                    Downloading...
                  </>
                ) : (
                  "Download Export"
                )}
              </Button>
              {downloadError && (
                <p className="text-sm text-destructive mt-2">
                  {downloadError}
                </p>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card className="border-primary/50" data-testid="data-export-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DownloadIcon className="h-5 w-5" />
                Export Your Data
              </CardTitle>
              <CardDescription>
                {isExportInProgress
                  ? "Preparing your export... You'll receive an email when it's ready."
                  : "Download a copy of all your data in JSON format. Includes your profile, linked accounts, and activity history."}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={() => {
                  setExportError(null);
                  exportMutation.mutate();
                }}
                disabled={
                  exportMutation.isPending || !!isExportInProgress
                }
                data-testid="export-data-btn"
              >
                {exportMutation.isPending || isExportInProgress ? (
                  <>
                    <SpinnerIcon className="h-4 w-4 mr-2 animate-spin" />
                    {isExportInProgress
                      ? "Export in progress..."
                      : "Requesting..."}
                  </>
                ) : (
                  "Export My Data"
                )}
              </Button>
              {exportError && (
                <p className="text-sm text-destructive mt-2">{exportError}</p>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Danger Zone — Account Deletion (AC#1) */}
      <div className="mt-8">
        <h2 className="text-lg font-semibold text-destructive mb-4">
          Danger Zone
        </h2>

        {isPendingDeletion ? (
          <Card className="border-green-500" data-testid="cancel-deletion-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-700 dark:text-green-400">
                <TrashIcon className="h-5 w-5" />
                Cancel Account Deletion
              </CardTitle>
              <CardDescription>
                Your account is scheduled for deletion on{" "}
                {accountStatus?.deletion_date
                  ? new Date(accountStatus.deletion_date).toLocaleDateString(
                      "en-US",
                      {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      }
                    )
                  : "an upcoming date"}.
                Cancel to keep your account active.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                variant="default"
                className="bg-green-600 hover:bg-green-700 text-white"
                onClick={() => cancelDeletionMutation.mutate()}
                disabled={cancelDeletionMutation.isPending}
                data-testid="cancel-deletion-btn"
              >
                {cancelDeletionMutation.isPending
                  ? "Cancelling..."
                  : "Cancel Deletion"}
              </Button>
              {cancelDeletionMutation.isError && (
                <p className="text-sm text-destructive mt-2">
                  {cancelDeletionMutation.error.message}
                </p>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card
            className="border-destructive/50"
            data-testid="delete-account-card"
          >
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-destructive">
                <TrashIcon className="h-5 w-5" />
                Delete my account
              </CardTitle>
              <CardDescription>
                Permanently delete your account and all data. This action cannot
                be undone after the 14-day grace period.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                variant="destructive"
                onClick={() => setDeleteDialogOpen(true)}
                data-testid="open-delete-dialog-btn"
              >
                Delete my account
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      <DeleteAccountDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        deletionCancelledAt={accountStatus?.deletion_cancelled_at}
      />
    </div>
  );
}

function formatExpiry(readyAt: string): string {
  const expiryDate = new Date(
    new Date(readyAt).getTime() + 24 * 60 * 60 * 1000
  );
  const now = new Date();
  const diffMs = expiryDate.getTime() - now.getTime();

  if (diffMs <= 0) return "soon";

  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

  if (diffHours > 0) {
    return `in ${diffHours}h ${diffMinutes}m`;
  }
  return `in ${diffMinutes}m`;
}

function ShieldIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
    </svg>
  );
}

function LockIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function DownloadIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" x2="12" y1="15" y2="3" />
    </svg>
  );
}

function SpinnerIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  );
}

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M3 6h18" />
      <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
      <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
      <line x1="10" x2="10" y1="11" y2="17" />
      <line x1="14" x2="14" y1="11" y2="17" />
    </svg>
  );
}
